import { Icon } from "@iconify/react";
import { Button, Checkbox, Label, Modal, TextInput } from "flowbite-react";
import './style.css';
import Box from '@mui/material/Box';
import { useCallback, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import Image from './extensions/image-ext';
import { Indent } from "./extensions/indent";
import Youtube from "@tiptap/extension-youtube";
import LinkInputModal from './extensions/modal';
import { colors } from "@mui/material";
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';

// Mở rộng tiện ích Table để thêm đường viền màu đen
const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: 'border: 1px solid black; border-collapse: collapse;',
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['table', { ...HTMLAttributes, style: 'border: 1px solid black; border-collapse: collapse;' }, 0];
  },
});

// Mở rộng TableCell để thêm đường viền màu đen
const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: 'border: 1px solid black;',
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['td', { ...HTMLAttributes, style: 'border: 1px solid black;' }, 0];
  },
});

// Mở rộng TableHeader để thêm đường viền màu đen
const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: 'border: 1px solid black;',
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['th', { ...HTMLAttributes, style: 'border: 1px solid black;' }, 0];
  },
});

interface IPros {
  children: any;
  onBlur: VoidFunction;
}

const ChildrenBlur = ({ children, onBlur, ...props }: IPros) => {
  const handleBlur = useCallback(
    (e: any) => {
      const currentTarget = e.currentTarget;
      requestAnimationFrame(() => {
        if (!currentTarget.contains(document.activeElement)) {
          onBlur();
        }
      });
    },
    [onBlur]
  );

  return (
    <div {...props} onBlur={handleBlur}>
      {children}
    </div>
  );
};

interface IProps {
  data: string;
  setData?: (data: string) => void;
  height?: number | 200;
  editable?: boolean | true;
  showMask?: number | false;
  isBold?: boolean;
}

const MenuBar = ({ editor, pos, input }: { editor: Editor | null; pos: number; input: any }) => {
  const [openLink, setOpenLink] = useState<boolean>(false);
  const [openYoutube, setOpenYoutube] = useState<boolean>(false);
  const [openTableModal, setOpenTableModal] = useState<boolean>(false);
  const [youtubeWidth, setYoutubeWidth] = useState(320);
  const [youtubeHeight, setYoutubeHeight] = useState(180);
  const [url, setUrl] = useState('');

  const [openPen, setOpenPen] = useState<boolean>(false);
  if (!editor) {
    return null;
  }

  const setTextColor = (color: string) => {
    if (color === 'unset') {
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().setColor(color).run();
    }
    setOpenPen(false);
  };

  const setUnsetLink = () => {
    const previousUrl = editor.getAttributes("link").href || '';
    setUrl(previousUrl);
    setOpenLink(true);
  };

  const addYoutubeVideo = () => {
    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("youtube").clearContent().run();
      return;
    }

    editor.commands.setYoutubeVideo({
      src: url,
      width: youtubeWidth < 320 ? 320 : youtubeWidth,
      height: youtubeHeight < 180 ? 180 : youtubeHeight,
    });
  };

  const handleUploadImage = async (event: any) => {
    const file = event.target.files[0];
    const fileSize = file.size / (1024 * 1024);
    if (fileSize > 3) {
      alert("Chỉ nhận file nhỏ hơn 3MB");
      return false;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const image = document.createElement('img');
      image.onload = async () => {
        // @ts-ignore
        editor.chain().focus().setImage({ src: e.target.result }).run();
      };
      // @ts-ignore
      image.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-bold" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-italic" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-strikethrough" />
        </button>
      </div>
      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-h1" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-h2" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-h3" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          className={editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-h4" />
        </button>
      </div>
      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-list-bulleted" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-list-numbered" />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.commands.indent();
          }}
        >
          <Icon icon="material-symbols:format-indent-increase" />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.commands.outdent();
          }}
        >
          <Icon icon="material-symbols:format-indent-decrease" />
        </button>
      </div>
      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={editor.isActive('codeBlock') ? 'is-active' : ''}
        ><Icon icon="material-symbols:code-blocks" /></button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'is-active' : ''}
        ><Icon icon="material-symbols:format-quote" /></button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        ><Icon icon="material-symbols:horizontal-rule" /></button>
      </div>
      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-align-left" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-align-center" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-align-right" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
        >
          <Icon icon="material-symbols:format-align-justify" />
        </button>
      </div>
      <div className="group">
        <button>
          <input
            type="color"
            style={{ width: '32px', height: '32px', border: 'none', cursor: 'pointer' }}
            onInput={(event) =>
              editor
                .chain()
                .focus()
                .setColor((event.target as HTMLInputElement).value)
                .run()
            }
            onChange={(event) =>
              editor
                .chain()
                .focus()
                .setColor((event.target as HTMLInputElement).value)
                .run()
            }
            value={editor.getAttributes('textStyle').color || '#000000'}
            data-testid="setColor"
          />
        </button>
      </div>
      <div className="group">
        <LinkInputModal title="Insert Link" openModal={openLink} onClose={() => { setOpenLink(false) }}>
          <div className="space-y-6">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="url" value="URL" />
              </div>
              <TextInput
                id="url"
                placeholder="https://"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
              />
            </div>
            <div className="w-full">
              <Button
                onClick={() => {
                  setOpenLink(false);
                  if (!url) {
                    editor.chain().focus().unsetLink().run();
                    return;
                  }
                  const { from, to } = editor.state.selection;
                  if (from !== to) {
                    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                    setTimeout(() => {
                      editor.chain().focus().setColor('#0000FF').run();
                    }, 50);
                  }
                }}
              >
                Ok
              </Button>
            </div>
          </div>
        </LinkInputModal>
        <button
          type="button"
          onClick={setUnsetLink}
        >
          {editor.isActive("link") ? <Icon icon="material-symbols:link-off" /> : <Icon icon="material-symbols:link" />}
        </button>
        <LinkInputModal title="Insert Youtube Video" openModal={openYoutube} onClose={() => { setOpenYoutube(false) }}>
          <div className="space-y-6">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="url" value="URL" />
              </div>
              <TextInput
                id="url"
                placeholder="https://"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
              />
            </div>
            <div>
              <div className="mb-2 block">
                <Label htmlFor="width" value="Width" />
              </div>
              <TextInput
                id="width"
                placeholder="Width"
                value={youtubeWidth}
                onChange={(event) => setYoutubeWidth(parseInt(event.target.value))}
              />
            </div>
            <div>
              <div className="mb-2 block">
                <Label htmlFor="height" value="Height" />
              </div>
              <TextInput
                id="height"
                placeholder="Height"
                value={youtubeHeight}
                onChange={(event) => setYoutubeHeight(parseInt(event.target.value))}
              />
            </div>
            <div className="w-full">
              <Button onClick={() => {
                addYoutubeVideo();
                setOpenYoutube(false);
              }}>Ok</Button>
            </div>
          </div>
        </LinkInputModal>
        <button
          type="button"
          onClick={() => { setOpenYoutube(true) }}
        >
          <Icon icon="material-symbols:youtube-activity" />
        </button>
        <button
          type="button"
          onClick={(e: any) => {
            const inputFile = e.target.closest('button').children[1];
            inputFile.click();
          }}
        >
          <Icon icon="material-symbols:image-outline" color="black" />
          <input
            accept="image/*"
            type="file"
            style={{
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              height: 1,
              overflow: 'hidden',
              position: 'absolute',
              bottom: 0,
              left: 0,
              whiteSpace: 'nowrap',
              width: 1,
            }}
            onChange={handleUploadImage}
          />
        </button>
        <button
          type="button"
          onClick={() => setOpenTableModal(true)}
        >
          <Icon icon="material-symbols:table" />
        </button>
      </div>
      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().run()}
          title="Clear Format"
        >
          <Icon icon="material-symbols:format-clear" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
        >
          <Icon icon="material-symbols:undo" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
        >
          <Icon icon="material-symbols:redo" />
        </button>
      </div>
      <TableInputModal openModal={openTableModal} onClose={() => setOpenTableModal(false)} editor={editor} />
    </>
  );
};

interface TableInputModalProps {
  openModal: boolean;
  onClose: () => void;
  editor: Editor;
}

const TableInputModal: React.FC<TableInputModalProps> = ({ openModal, onClose, editor }) => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);

  const handleCreateTable = () => {
    const { state } = editor;
    const { selection } = state;
    const { $from } = selection;

    // Check if the current selection is inside a table
    let pos = $from.pos;
    let node = $from.node(-1); // Get the parent node
    let isInsideTable = false;

    // Traverse up the document tree to check if we're inside a table
    for (let depth = $from.depth; depth > 0; depth--) {
      node = $from.node(depth);
      if (node.type.name === 'table') {
        isInsideTable = true;
        pos = $from.after(depth); // Position after the table
        break;
      }
    }

    // If inside a table, move the selection outside
    if (isInsideTable) {
      editor
        .chain()
        .setTextSelection(pos)
        .insertContent('<p></p>') // Insert a paragraph to ensure separation
        .focus()
        .insertTable({ rows, cols })
        .run();
    } else {
      // If not inside a table, insert the table directly
      editor
        .chain()
        .focus()
        .insertTable({ rows, cols })
        .run();
    }

    onClose();
  };

  return (
    <Modal show={openModal} onClose={onClose}>
      <Modal.Header>Tạo bảng</Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          <div>
            <Label htmlFor="rows" value="Hàng" />
            <TextInput
              id="rows"
              type="number"
              value={rows}
              onChange={(e) => setRows(parseInt(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="cols" value="Cột" />
            <TextInput
              id="cols"
              type="number"
              value={cols}
              onChange={(e) => setCols(parseInt(e.target.value))}
            />
          </div>
          <Button onClick={handleCreateTable}>Tạo bảng</Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const TextEditor = ({ data, setData, height, editable, showMask, isBold }: IProps) => {
  const [update, setUpdate] = useState(false);
  const [lastPos, setLastPos] = useState(1);
  const inputRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [offsetWidth, setOffsetWidth] = useState(0);
  const [offsetHeight, setOffsetHeight] = useState(0);

  useLayoutEffect(() => {
    if (containerRef?.current) {
      // console.log(containerRef.current.offsetWidth, containerRef.current.offsetHeight)
    }
  }, []);
  useEffect(() => {
    setOffsetWidth(containerRef.current?.offsetWidth || 1024);
    setOffsetHeight(containerRef.current?.offsetHeight || 40);
  }, [containerRef.current?.offsetHeight]);

  const txtEditor = useEditor(
    {
      extensions: [
        StarterKit,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Image.configure({
          inline: true,
          allowBase64: true,
        }),
        TextStyle,
        Color.configure({
          types: ['textStyle'],
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
        }),
        Youtube.configure({
          inline: false,
          nocookie: true
        }),
        Indent,
        CustomTable.configure({
          resizable: true,
        }),
        TableRow,
        CustomTableCell,
        CustomTableHeader,
      ],
      editable: editable ?? true,
      content: data,
      onBlur: ({ editor }) => {
        // setData(editor.getHTML());
      },
      onUpdate: ({ editor }) => {
        // setData(editor.getHTML());
        setUpdate(true);
      },
      onTransaction: ({ editor }) => {
        if (editor.view.hasFocus()) {
          setLastPos(editor.view.state.selection.$anchor.pos);
        }
      },
    },
    [data]
  );

  const handleBlur = () => {
    if (txtEditor && update) {
      setUpdate(false);
      if (setData) setData(txtEditor.getHTML());
    }
  };

  return (
    <ChildrenBlur onBlur={handleBlur}>
      <div className="tiptap" ref={containerRef}>
        <div
          className={editable !== false ? 'container border rounded-border' : 'container readonly'}
        >
          {editable !== false && (
            <div className="toolbar rounded-border">
              <MenuBar editor={txtEditor} pos={lastPos} input={inputRef} />
            </div>
          )}
          <div
            className="wrapper"
            style={{
              display: 'inline-block',
              visibility: showMask ? 'hidden' : undefined,
              marginBottom: showMask ? '30px' : '0px',
            }}
          >
            <EditorContent
              editor={txtEditor}
              className="tiptap-editor"
              height={height}
              ref={inputRef}
              style={{ fontWeight: isBold ? 500 : 'normal' }}
            />
          </div>
        </div>
        {showMask && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              marginTop: '0!important',
              zIndex: 10,
              display: 'inline-block',
              backgroundImage: `url(/assets/background/${showMask === 2 ? 'paper-line2.png' : 'paper-line.png'})`,
              backgroundRepeat: 'repeat',
              backgroundColor: 'white',
              minHeight: '40px',
              width: `${offsetWidth}px`,
              height: `${offsetHeight}px`,
            }}
          />
        )}
      </div>
    </ChildrenBlur>
  );
};

export default TextEditor;